import { useEffect, useState } from 'react';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Slices. */
import { setHistoryList } from 'shared/store/slices/historySlice';

/** Internal Components. */
import { readSocketData } from 'shared/lib/fetchSocket';

/**
 * Module-level so the cache survives detail panel remounts: the grid virtualizer
 * unmounts panels scrolled out of view, and a per-instance cache would trigger a
 * refetch (and an empty-panel flash that collapses the measured panel height,
 * yanking the scroll position) every time the panel scrolls back in. Fetched rows
 * stay fresh through live socket pushes merged into historyList.
 */
const fetchedKeys = new Set();

/**
 * Broadcasts missed while the socket was down are lost for good, so on every
 * (re)connection the connect handler drops the cache: the next mount of each
 * panel re-reads its full history once and heals any gap.
 */
export const clearFetchedHistoryCache = () => {
    fetchedKeys.clear();
};

/**
 * Fetches il_history for a given shipment row (once per tabValue/rowId pair)
 * and keeps it registered in the shared historyList slice.
 */
export default function useFetchHistory(tabValue, rowId) {
    const dispatch = useDispatch();
    const historyList = useSelector((state) => state.historyList);
    const [isReady, setIsReady] = useState(() => fetchedKeys.has(`${tabValue}-${rowId}`));

    useEffect(() => {
        if (!rowId || tabValue == null) return undefined;

        /**
         * Only fetchedKeys (the session cache) gates the fetch.
         * Checking historyList for an existing entry is unsafe: a single item can land there
         * via a live socket push (another edit broadcast) before this row's full history was
         * ever read, which would wrongly skip the real backend read and leave the list incomplete.
         */
        const cacheKey = `${tabValue}-${rowId}`;
        if (fetchedKeys.has(cacheKey)) {
            setIsReady(true);
            return undefined;
        }
        setIsReady(false);

        let cancelled = false;

        readSocketData(
            dispatch,
            tabValue,
            'cell',
            'il_history',
            null,
            rowId,
            (receivedData) => {
                if (cancelled) return;
                dispatch(setHistoryList({ tabValue, data: receivedData }));
                fetchedKeys.add(cacheKey);
            },
            null,
            () => {
                if (!cancelled) setIsReady(true);
            },
        );

        return () => {
            cancelled = true;
        };
    }, [rowId, tabValue, dispatch]);

    return { historyList, isReady };
}
